import { Link } from "@tanstack/react-router";
import lux from "@/assets/1742561569_3593189334595606656.webp";
import artisan from "@/assets/1780018027_3907396849912522036.webp";
import decor from "@/assets/1779947897_3906808566430270185.webp";
import romantic from "@/assets/1779847500_3905966368420069795.webp";
import { useState, useEffect } from "react";
import { API_BASE_URL } from "@/lib/api";

const staticCollections = [
  { name: "Luxury Collection", count: "10 Products", img: lux, search: { category: "Luxury" as const } },
  { name: "Artisan Collection", count: "12 Products", img: artisan, search: { category: "Artisan" as const } },
  { name: "Decor Collection", count: "15 Products", img: decor, search: { category: "Decorative" as const } },
  { name: "Romantic Collection", count: "18 Products", img: romantic, search: { color: "rose" as const } },
];

export function Collections() {
  const [dbCollections, setDbCollections] = useState<any[]>([]);

  useEffect(() => {
    const fetchHomeCollections = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/collections`);
        if (res.ok) {
          const data = await res.json();
          setDbCollections(data.slice(0, 4));
        } else {
          setDbCollections(staticCollections);
        }
      } catch (err) {
        setDbCollections(staticCollections);
      }
    };
    fetchHomeCollections();
  }, []);

  const items = dbCollections.length > 0 ? dbCollections : staticCollections;

  const resolveImg = (imgUrl: string | undefined, fallback: string) => {
    if (!imgUrl) return fallback;
    if (imgUrl.includes("1742561569")) return lux;
    if (imgUrl.includes("1780018027")) return artisan;
    if (imgUrl.includes("1779947897")) return decor;
    if (imgUrl.includes("1779847500")) return romantic;
    if (imgUrl.startsWith("http://") || imgUrl.startsWith("https://") || imgUrl.startsWith("/assets/")) return imgUrl;
    return fallback;
  };

  return (
    <section id="collections" className="py-14 lg:py-24">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="text-center mb-10 lg:mb-14">
          <p className="eyebrow">Explore Our Range</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl">
            Shop by <span className="italic text-gold">Collection</span>
          </h2>
          <div className="mx-auto mt-5 h-px w-20 bg-gold" />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 lg:gap-8">
          {items.map((c, idx) => {
            const search = c.search || { collection: c.slug };
            const countLabel = c.products !== undefined 
              ? `${c.products.length} Products` 
              : c._count?.products !== undefined 
                ? `${c._count.products} Products` 
                : (c.count || "0 Products");

            const fallbacks = [lux, artisan, decor, romantic];
            const resolvedImage = resolveImg(c.img, fallbacks[idx % fallbacks.length]);

            return (
              <Link
                key={c.name}
                to="/shop"
                search={search}
                className="group relative aspect-[3/4] rounded-[14px] sm:rounded-[20px] overflow-hidden shadow-luxe hover:shadow-luxe-hover transition-all duration-500"
              >
                <img
                  src={resolvedImage}
                  alt={c.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = fallbacks[idx % fallbacks.length];
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent" />
                <div className="absolute inset-0 rounded-[14px] sm:rounded-[20px] ring-1 ring-inset ring-gold/0 group-hover:ring-gold/60 transition-all duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6 text-cream">
                  <div className="text-[0.55rem] sm:text-[0.65rem] tracking-[0.25em] sm:tracking-[0.3em] uppercase text-gold/90">Collection</div>
                  <h3 className="font-display text-sm sm:text-2xl mt-0.5 sm:mt-1">{c.name}</h3>
                  <div className="mt-0.5 sm:mt-1 text-[0.65rem] sm:text-xs text-cream/70">{countLabel}</div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 lg:mt-14 text-center">
          <Link to="/collections" className="btn-outline-dark">View All Collections</Link>
        </div>
      </div>
    </section>
  );
}
